import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';

type Props = {
  title?: string;
  overrideTitleTemplate?: boolean;
  description?: string;
  imageUrl?: string;
  meta?: Array<{
    name: string;
    content: string;
  }>;
};

const PageHead = ({
  title,
  overrideTitleTemplate,
  imageUrl,
  description,
  meta,
}: Props) => {
  const { pathname } = useLocation();
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            defaultSiteTitle
            description
            author
            twitter
            lang
            locale
            siteUrl
          }
        }
      }
    `,
  );

  const defaultTitle = site.siteMetadata?.title;
  const defaultSiteTitle = site.siteMetadata?.defaultSiteTitle;
  const metaDescription = description || site.siteMetadata.description;
  const { lang, locale, siteUrl } = site.siteMetadata;
  const openGraphImage = imageUrl || '/images/stackscout-image.png';
  const metaTags = [
    {
      name: `description`,
      content: metaDescription,
    },
    {
      property: `og:site_name`,
      content: site.siteMetadata?.title,
    },
    {
      property: `og:title`,
      content: title,
    },
    {
      property: `og:url`,
      content: `${siteUrl}${pathname}`,
    },
    {
      property: `og:image`,
      content: `${siteUrl}${openGraphImage}`,
    },
    {
      property: `twitter:image`,
      content: `${siteUrl}${openGraphImage}`,
    },
    {
      property: `image`,
      content: `${siteUrl}${openGraphImage}`,
    },
    {
      property: `og:description`,
      content: metaDescription,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: `og:locale`,
      content: locale,
    },
    {
      name: `twitter:card`,
      content: `summary_large_image`,
    },
    {
      name: `twitter:creator`,
      content: site.siteMetadata?.twitter || ``,
    },
    {
      name: `twitter:title`,
      content: title,
    },
    {
      name: `twitter:description`,
      content: metaDescription,
    },
    {
      name: 'robots',
      content: 'all',
    },
  ]
    .concat(meta)
    .filter(Boolean);

  return (
    <>
      <html lang={lang} />
      <body className="font-sans text-base antialiased text-slate-700" />
      <title>
        {defaultTitle && title && !overrideTitleTemplate
          ? `${title} - ${defaultTitle}`
          : title ?? defaultSiteTitle}
      </title>
      {metaTags.map((metaTag) => (
        <meta
          key={metaTag.name ?? metaTag.property}
          name={metaTag.name}
          property={metaTag.property}
          content={metaTag.content}
        />
      ))}
    </>
  );
};

export default PageHead;
